import React from "react";
import {
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Divider,
  Paper,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  BadgeMark,
  BadgeRoot,
} from "@mui/material";
import { formatDisplayDate } from "utils/dateFormat";
import {
  Person,
  Email,
  Phone,
  CalendarToday,
  Favorite,
  LocationOn,
  Badge,
  Event,
  People,
  NumbersOutlined,
  Person2,
} from "@mui/icons-material";

const DownloadClientData = ({ printData, contentRef }) => {
  if (!printData) return null;

  return (
    <div
      ref={contentRef}
      className="bg-app-surface p-16 rounded-xl shadow-lg max-w-6xl mx-auto text-app-text font-sans h-screen"
    >
      {/* Header */}
      <div className="text-center mb-6 border-b border-app-border pb-4">
        <div className="flex items-center justify-center mb-3">
          <div className="w-12 h-12 bg-app-primary rounded-full flex items-center justify-center mr-3">
            <Person className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-app-text">Client Profile</h1>
            <p className="text-sm text-app-text-secondary">Detailed Information</p>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-2 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Personal Information Section */}
          <div>
            <div className="flex items-center mb-3">
              <Person className="w-4 h-4 text-primary mr-2" />
              <h2 className="text-lg font-semibold text-app-text">Personal Information</h2>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div className="bg-app-surface-muted p-3 rounded-lg">
                <div className="flex items-center mb-1">
                  <Person className="w-3 h-3 text-primary mr-2" />
                  <span className="text-xs font-medium text-app-text-secondary">Full Name</span>
                </div>
                <div className="text-sm font-semibold text-app-text">{printData.name || "-"}</div>
              </div>
              <div className="bg-app-surface-muted p-3 rounded-lg">
                <div className="flex items-center mb-1">
                  <Email className="w-3 h-3 text-primary mr-2" />
                  <span className="text-xs font-medium text-app-text-secondary">Email Address</span>
                </div>
                <div className="text-sm font-semibold text-app-text">{printData.email || "-"}</div>
              </div>{" "}
              <div className="bg-app-surface-muted p-3 rounded-lg">
                <div className="flex items-center mb-1">
                  <LocationOn className="w-3 h-3 text-green-600 mr-2" />
                  <span className="text-xs font-medium text-app-text-secondary">Address</span>
                </div>
                <div className="text-sm font-semibold text-app-text">
                  {printData.address || "-"}
                </div>
              </div>
              <div className="bg-app-surface-muted p-3 rounded-lg">
                <div className="flex items-center mb-1">
                  <Phone className="w-3 h-3 text-primary mr-2" />
                  <span className="text-xs font-medium text-app-text-secondary">Mobile Number</span>
                </div>
                <div className="text-sm font-semibold text-app-text">{printData.number || "-"}</div>
              </div>
              <div className="bg-app-surface-muted p-3 rounded-lg">
                <div className="flex items-center mb-1">
                  <CalendarToday className="w-3 h-3 text-primary mr-2" />
                  <span className="text-xs font-medium text-app-text-secondary">Date of Birth</span>
                </div>
                <div className="text-sm font-semibold text-app-text">{printData.dob || "-"}</div>
              </div>
              <div className="bg-app-surface-muted p-3 rounded-lg">
                <div className="flex items-center mb-1">
                  <Person className="w-3 h-3 text-primary mr-2" />
                  <span className="text-xs font-medium text-app-text-secondary">Gender</span>
                </div>
                <div className="text-sm font-semibold text-app-text capitalize">
                  {printData.gender || "-"}
                </div>
              </div>
              <div className="bg-app-surface-muted p-3 rounded-lg">
                <div className="flex items-center mb-1">
                  <NumbersOutlined className="w-3 h-3 text-primary mr-2" />
                  <span className="text-xs font-medium text-app-text-secondary">USI Number</span>
                </div>
                <div className="text-sm font-semibold text-app-text capitalize">
                  {printData.usiNumber || "-"}
                </div>
              </div>
            </div>
          </div>

          {/* Location Information Section */}
          <div>
            <div className="flex items-center mb-3">
              <Person2 className="w-4 h-4 text-green-600 mr-2" />
              <h2 className="text-lg font-semibold text-app-text">Emergency Contact Information</h2>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div className="bg-app-surface-muted p-3 rounded-lg">
                <div className="flex items-center mb-1">
                  <NumbersOutlined className="w-3 h-3 text-primary mr-2" />
                  <span className="text-xs font-medium text-app-text-secondary">
                    Emergency Contact Name
                  </span>
                </div>
                <div className="text-sm font-semibold text-app-text capitalize">
                  {printData.emergencyContactName || "-"}
                </div>
              </div>
              <div className="bg-app-surface-muted p-3 rounded-lg">
                <div className="flex items-center mb-1">
                  <NumbersOutlined className="w-3 h-3 text-primary mr-2" />
                  <span className="text-xs font-medium text-app-text-secondary">
                    Emergency Contact Number
                  </span>
                </div>
                <div className="text-sm font-semibold text-app-text capitalize">
                  {printData.emergencyContact || "-"}
                </div>
              </div>
              <div className="bg-app-surface-muted p-3 rounded-lg">
                <div className="flex items-center mb-1">
                  <NumbersOutlined className="w-3 h-3 text-primary mr-2" />
                  <span className="text-xs font-medium text-app-text-secondary">
                    Emergency Contact Email
                  </span>
                </div>
                <div className="text-sm font-semibold text-app-text ">
                  {printData.emergencyEmail || "-"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Visa Information Section */}
          <div>
            <div className="flex items-center mb-3">
              <Event className="w-4 h-4 text-purple-600 mr-2" />
              <h2 className="text-lg font-semibold text-app-text">Passport & Visa Information</h2>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div className="bg-app-surface-muted p-3 rounded-lg">
                <div className="flex items-center mb-1">
                  <Badge className="w-3 h-3 text-primary mr-2" />
                  <span className="text-xs font-medium text-app-text-secondary">
                    Passport Number
                  </span>
                </div>
                <div className="text-sm font-semibold text-app-text">
                  {printData.passportNumber || "-"}
                </div>
              </div>{" "}
              <div className="bg-app-surface-muted p-3 rounded-lg">
                <div className="flex items-center mb-1">
                  <Badge className="w-3 h-3 text-primary mr-2" />
                  <span className="text-xs font-medium text-app-text-secondary">
                    Passport Issued Date
                  </span>
                </div>
                <div className="text-sm font-semibold text-app-text">
                  {formatDisplayDate(printData.dateOfIssue, "-")}
                </div>
              </div>
              <div className="bg-app-surface-muted p-3 rounded-lg">
                <div className="flex items-center mb-1">
                  <Badge className="w-3 h-3 text-primary mr-2" />
                  <span className="text-xs font-medium text-app-text-secondary">
                    Passport Expiry
                  </span>
                </div>
                <div className="text-sm font-semibold text-app-text">
                  {formatDisplayDate(printData.passportExpiry, "-")}
                </div>
              </div>
              <div className="bg-app-surface-muted p-3 rounded-lg">
                <div className="flex items-center mb-1">
                  <Event className="w-3 h-3 text-purple-600 mr-2" />
                  <span className="text-xs font-medium text-app-text-secondary">Visa Status</span>
                </div>
                <div className="text-sm font-semibold text-app-text">
                  {printData.visaStatus || "-"}
                </div>
              </div>
              <div className="bg-app-surface-muted p-3 rounded-lg">
                <div className="flex items-center mb-1">
                  <Event className="w-3 h-3 text-purple-600 mr-2" />
                  <span className="text-xs font-medium text-app-text-secondary">Visa Expiry</span>
                </div>
                <div className="text-sm font-semibold text-app-text">
                  {printData.visaExpiry || "-"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      {/* <div className="text-center pt-4 border-t border-app-border mt-6">
        <p className="text-xs text-app-text-secondary">
          Generated on{" "}
          {new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>
      </div> */}
    </div>
  );
};

export default DownloadClientData;
